import React from 'react';
import { addHours, format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import VehicleMap from './VehicleMap';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Car, Gauge, Clock, Users, MapPin, X } from 'lucide-react';

const TeamLocationModal = ({ open, onOpenChange, team, data, isLoading }) => {
  if (!open) return null;

  const handleClose = () => {
    onOpenChange(false);
  };

  const location = data?.location || null;

  const formatCoordinate = (value) => {
    if (value == null) return '-';

    const num = Number(value);
    if (Number.isNaN(num)) return value;

    return num.toFixed(6);
  };

  const formatTimestamp = (value) => {
    if (!value) return '-';

    try {
      const raw = String(value).trim();
      const date =
        value instanceof Date
          ? value
          : parseISO(raw);

      if (Number.isNaN(date.getTime())) return value;

      const turkeyTime = addHours(date, 3);

      return format(turkeyTime, 'dd.MM.yyyy HH:mm', { locale: tr });
    } catch {
      return value;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-3xl px-4 animate-slide-up max-h-[90vh] overflow-y-auto">
        <Card className="relative border border-slate-200 bg-white text-slate-900 shadow-2xl rounded-2xl overflow-hidden">
          <CardHeader className="px-5 py-4 border-b border-slate-200">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600">
              Araç Konumu
            </p>
            <CardTitle className="mt-1 text-base font-semibold text-slate-900">
              {location?.address || 'Konum bilgisi'}
            </CardTitle>
            {team && (
              <p className="mt-1 text-xs text-slate-500">
                {team.team_name || team.team_code || '-'}
                {team.ambulance_plate ? ` · ${team.ambulance_plate}` : ''}
              </p>
            )}
          </CardHeader>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="absolute right-4 top-4 h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-4 w-4" />
          </Button>
          <CardContent className="space-y-4 px-5 pb-5 pt-4">
            {isLoading || !location ? (
              <div className="h-72 md:h-80 flex items-center justify-center">
                <div className="text-sm text-slate-500">
                  Araç konumu yükleniyor...
                </div>
              </div>
            ) : (
              <>
                <div className="h-72 md:h-80 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                  <VehicleMap
                    latitude={location.latitude}
                    longitude={location.longitude}
                    radius={80}
                    zoom={17}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs sm:text-sm">
                  <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-3 flex flex-col gap-1">
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <Car className="h-4 w-4 text-blue-600" />
                        Plaka
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {data?.ambulance_plate || 'Tanımsız'}
                    </div>
                  </div>

                  <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-3 flex flex-col gap-1">
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <Gauge className="h-4 w-4 text-blue-600" />
                        Hız
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      {location.speed != null ? `${location.speed} km/s` : '-'}
                    </div>
                  </div>

                  <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-3 flex flex-col gap-1">
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4 text-blue-600" />
                        Son Güncelleme
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      {formatTimestamp(location.timestamp)}
                    </div>
                  </div>

                  <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-3 flex flex-col gap-1">
                    <div className="flex items-center justify-between text-[11px] text-slate-500">
                      <span className="flex items-center gap-1.5">
                        <Users className="h-4 w-4 text-blue-600" />
                        Ekip
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {team?.team_name || team?.team_code || '-'}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-3 flex gap-3">
                  <div className="mt-0.5">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                      Adres
                    </div>
                    <div className="text-sm text-slate-800">
                      {location.address || 'Adres bilgisi bulunamadı.'}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-t border-slate-200 pt-3 text-[11px] text-slate-500">
                  <div className="space-x-4">
                    <span>
                      Enlem:{' '}
                      <span className="font-mono text-slate-800">
                        {formatCoordinate(location.latitude)}
                      </span>
                    </span>
                    <span>
                      Boylam:{' '}
                      <span className="font-mono text-slate-800">
                        {formatCoordinate(location.longitude)}
                      </span>
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClose}
                    className="mt-2 sm:mt-0 sm:ml-auto"
                  >
                    Kapat
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeamLocationModal;

